import { useEffect, useRef } from "react";
import { useAppDispatch } from "store/hooks";
import {
  setErrorsInLayoutEditor,
  setErrorsInSchemaEditor,
} from "../store/codeEditor";
import { EditorVariant, ErrorData } from "../types";

const useValidationDedicatedWorker = (target: EditorVariant) => {
  const worker = useRef<Worker>();
  const validateFn =
    useRef<
      (
        validationObject: unknown,
        schemaObject: any,
        jsonObject: any,
        jsonLines: string[]
      ) => void
    >();
  const dispatch = useAppDispatch();

  const handleMessage = (e: MessageEvent<ErrorData[]>) => {
    switch (target) {
      case EditorVariant.Schema:
        dispatch(setErrorsInSchemaEditor(e.data));
        break;
      case EditorVariant.Layout:
        dispatch(setErrorsInLayoutEditor(e.data));
        break;
    }
  };

  useEffect(() => {
    if (!worker.current) {
      switch (target) {
        case EditorVariant.Schema:
          worker.current = new Worker(
            new URL(`../workers/backgroundSchemaValidator.ts`, import.meta.url)
          );
          break;
        case EditorVariant.Layout:
          worker.current = new Worker(
            new URL(`../workers/backgroundLayoutValidator.ts`, import.meta.url)
          );
          break;
      }

      validateFn.current = (
        validationObject: unknown, // This is an object that follows the schema structure
        schemaObject: any, // This is the schema definition object
        jsonObject: any, // This is the editor content as object
        jsonLines: string[] // These are the lines of the content in the schema editor
      ) => {
        worker?.current?.postMessage({
          validationObject,
          schemaObject,
          jsonObject,
          jsonLines,
        });
      };
    }
    worker?.current?.addEventListener("message", handleMessage);
    return () => {
      worker?.current?.removeEventListener("message", handleMessage);
    };
  });

  return validateFn;
};

export default useValidationDedicatedWorker;
